import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { useFormat } from "../utils";
import { toast } from "@shared/ui/use-toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { logEvent } from "@shared/analytics/analytics";

interface FieldConfigurationProps {
  field: any | null;
}

const FieldConfiguration = ({ field }: FieldConfigurationProps) => {
  if (!field) {
    return (
      <div className="text-center p-6 text-gray-500">
        <p>Select a field from the list to configure it</p>
        <p className="text-sm mt-2">
          Choose any field to customize its settings and behavior
        </p>
      </div>
    );
  }
  const remoteUtilityId = useRemoteUtilityId();
  const { state, dispatch } = useFormat();

  const [dropdownOptions, setDropdownOptions] = useState<any[]>([]);
  const [disableAndRequired, setDisableAndRequired] = useState<boolean>(false);

  const configuredField = state.selectedFields.find((f) => f.id === field.id);

  // console.log("debug configirures", {configuredField, defaultFieldValues});
  // Fetch choices data
  const { data: fieldTypes } = useChoices({
    choice: "field_type",
    remoteUtilityId,
  });
  const { data: inputType } = useChoices({
    choice: "input_type",
    remoteUtilityId,
  });
  const { data: displayType } = useChoices({
    choice: "display_type",
    remoteUtilityId,
  });
  const { data: readingStatus } = useChoices({
    choice: "meter_reading_status",
    remoteUtilityId,
  });
  const { data: reasonCodes } = useChoices({
    choice: "reason_code",
    remoteUtilityId,
  });

  if (!field) return null;

  // Transform choices data to options format
  const fieldTypeOptions =
    fieldTypes?.map((type) => ({
      value: type.key,
      label: type.value,
    })) || [];

  const inputTypeOptions =
    inputType?.map((type) => ({
      value: type.key,
      label: type.value,
    })) || [];

  const displayTypeOptions =
    displayType?.map((type) => ({
      value: type.key,
      label: type.value,
    })) || [];

  const readingStatusList = mapToLabelValue(readingStatus);
  const reasonCodeList = mapToLabelValue(reasonCodes);

  // Define form fields
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "inputMethod",
        type: "select",
        label: "Input Method",
        required: true,
        placeholder: "Select input method",
        fullWidth: true,
        options: inputTypeOptions,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "fieldType",
        type: "select",
        label: "Field Type",
        required: !disableAndRequired,
        disabled: disableAndRequired,
        helperText: "Select field type for manual input method.",
        placeholder: "Select Field type",
        fullWidth: true,
        options: fieldTypeOptions,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white ${
            disableAndRequired
              ? "cursor-not-allowed bg-gray-100"
              : "cursor-pointer"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-sm text-gray-600",
        },
      },
      {
        name: "values",
        type: "multi-select",
        label: "Options",
        required: false,
        options: dropdownOptions,
        dependsOn: "fieldType",
        showWhen: (formValues) =>
          formValues.fieldType === "DROPDOWN" &&
          (field.fieldName === "Reason Code" ||
            field.fieldName === "Reading Status"),
      },
      {
        name: "displayType",
        type: "select",
        label: "Display Type",
        required: true,
        placeholder: "Select display type",
        fullWidth: true,
        options: displayTypeOptions,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "required",
        type: "switch",
        label: "Required Field",
        required: false,
        fullWidth: true,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input: "mr-2",
          error: "text-red-600 text-sm mt-1",
        },
      },
    ],
    [fieldTypeOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // STEP 2 — Hydrate the form after config is available
  // useEffect(() => {
  //   if (configuredField) {
  //     FormService.populateForm(form, configuredField, formFields);
  //     const prefilledData= FormService.populateForm(form, configuredField, formFields);
  //     console.log('Onkar prefilled dara is',prefilledData)
  //     const selectedValues = mapToLabelValue(configuredField.values);
  //     form.setValue("values", selectedValues);
  //   }
  // }, [configuredField]);

  // Move the form reset logic to depend on both configuredField AND dropdownOptions

  // Keep the dropdown options effect as is, but also trigger form reset when needed
  useEffect(() => {
    if (!field) return;

    if (field.fieldName === "Reason Code" && reasonCodeList?.length) {
      setDropdownOptions(reasonCodeList);
    } else if (
      field.fieldName === "Reading Status" &&
      readingStatusList?.length
    ) {
      setDropdownOptions(readingStatusList);
    }
  }, [field?.fieldName, reasonCodeList, readingStatusList]);

  // Add this additional effect to handle cases where configuredField exists but we need to wait for options
  useEffect(() => {
    if (configuredField && dropdownOptions.length === 0) {
      // If we have configured field but no dropdown options yet, reset form with basic data
      form.reset({
        inputMethod: configuredField.inputMethod,
        fieldType: configuredField.fieldType,
        values: [], // Clear values until options are available
        displayType: configuredField.displayType,
        required: configuredField.required,
      });
    }
  }, [configuredField]);

  // Handle form submission
  const handleSubmit = async (data: any) => {
    const transformedValues = (data.values || []).map((val: any) => {
      console.log("Transformed Value:", val);
      const matched = dropdownOptions.find((opt) => opt.value === val);
      console.log("Transformed matched:", matched);

      return { key: matched.value, value: matched.label };
    });

    const updatedField = {
      ...field,
      ...data,
      values: transformedValues,
      required: data.required ?? false,
    };

    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        field: updatedField,
      },
    });
    logEvent("Format Field Configured", {
      ...updatedField,
    });

    toast({
      title: "Success",
      description: 'Configurations Applied',
      variant: "default",
    });
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
    switch (fieldName) {
      case "inputMethod": {
        const isAutomated = value.toLowerCase() === "automated";

        setDisableAndRequired(isAutomated);

        if (isAutomated) {
          form.setValue("fieldType", "");
        }
        break;
      }
    }
  };
  console.log("debug required disable");
  // Handle form reset
  const handleReset = () => {
    form.reset();
  };
// Create a stable reference for the configured field data
const configuredFieldData = useMemo(() => {
  if (!configuredField) return null;
  
  return {
    id: configuredField.id,
    inputMethod: configuredField.inputMethod,
    fieldType: configuredField.fieldType,
    values: configuredField.values,
    displayType: configuredField.displayType,
    required: configuredField.required,
  };
}, [configuredField?.id, configuredField?.inputMethod, configuredField?.fieldType, configuredField?.displayType, configuredField?.required, JSON.stringify(configuredField?.values)]);

// Use the stable reference in the useEffect
useEffect(() => {
  if (!configuredFieldData) return;

  const shouldHaveDropdownOptions = 
    configuredFieldData.fieldType === "DROPDOWN" && 
    (field.fieldName === "Reason Code" || field.fieldName === "Reading Status");

  if (shouldHaveDropdownOptions && dropdownOptions.length === 0) {
    return;
  }

  const selectedValues = shouldHaveDropdownOptions 
    ? (configuredFieldData.values || []).map(val => val.key || val.value)
    : [];

  const formData = {
    inputMethod: configuredFieldData.inputMethod,
    fieldType: configuredFieldData.fieldType,
    values: selectedValues,
    displayType: configuredFieldData.displayType,
    required: configuredFieldData.required,
  };

  form.reset(formData);

  console.log("Form reset with:", JSON.stringify(formData, null, 2));
}, [configuredFieldData, dropdownOptions.length]); // Only depend on length, not the entire array

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold">{field.fieldName}</h2>
          </div>
          <p className="text-sm text-gray-600">
            Configure field properties and behavior
          </p>
        </div>
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        loading={false}
        config={{
          showProgress: false,
          layout: "vertical",
          submitButtonText: "Apply Changes",
          showResetButton: true,
          resetButtonText: "Cancel",
          resetOnSubmit: false,
          classes: {
            form: "space-y-4",
            fieldContainer: "",
            buttonContainer: "flex justify-end space-x-2 pt-4 border-t",
            fieldGroup: "",
          },
        }}
        className=""
        style={{
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      />
    </div>
  );
};

export default FieldConfiguration;
